import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  GraduationCap, PlayCircle, Gauge, Sparkles, ListChecks, MessageSquare, PenTool,
  Calendar, PartyPopper, Star, Users, TrendingUp, Target, LineChart, Send, Loader2, Lightbulb,
} from "lucide-react";
import { Layout } from "../components/Layout";
import { Markdown } from "../components/Markdown";
import { api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { HELP } from "../lib/helpContent";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";

const FN_ICONS = [Gauge, Sparkles, ListChecks, MessageSquare, PenTool, Calendar, PartyPopper, Star, Users, TrendingUp, Target, LineChart];

const stagger = { animate: { transition: { staggerChildren: 0.05 } } };
const item = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

export default function HelpCenter() {
  const { lang } = useLang();
  const H = HELP[lang] || HELP.fr;
  const L = H.labels;
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);

  const ask = async () => {
    if (!q.trim() || loading) return;
    setLoading(true); setAnswer("");
    try {
      const { data } = await api.post("/ai/help", { question: q });
      setAnswer(data.content);
    } catch { toast.error(H.smartError); }
    finally { setLoading(false); }
  };

  return (
    <Layout>
      <motion.div variants={stagger} initial="initial" animate="animate" className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <motion.div variants={item} className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center text-neon shrink-0"><GraduationCap size={22} /></div>
            <div>
              <h1 className="font-display font-black text-2xl lg:text-3xl">{H.title}</h1>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-0.5 max-w-2xl">{H.intro}</p>
            </div>
          </div>
          <Button data-testid="help-replay-tour" onClick={() => window.dispatchEvent(new CustomEvent("lb-open-tour"))}
            className="rounded-full h-10 px-4 text-sm bg-gradient-to-r from-primary to-neon text-white lb-glow-btn shrink-0">
            <PlayCircle size={16} className="mr-1.5" /> {H.tourCta}
          </Button>
        </motion.div>

        {/* Journey */}
        <motion.div variants={item} className="lb-card p-5" data-testid="help-journey">
          <h2 className="font-display font-bold text-lg mb-4">{H.journeyTitle}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {H.journey.map((s, i) => (
              <div key={i} className="flex gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/5">
                <div className="w-7 h-7 rounded-lg bg-neon/15 text-neon flex items-center justify-center text-sm font-bold shrink-0">{i + 1}</div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{s.title}</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] leading-relaxed mt-0.5">{s.text}</p>
                </div>
              </div>
            ))}
          </div>
          {H.journeyCycle && (
            <p className="text-xs text-neon/90 mt-4 text-center font-medium leading-relaxed" data-testid="help-journey-cycle">{H.journeyCycle}</p>
          )}
        </motion.div>

        {/* Functions */}
        <motion.div variants={item} className="lb-card p-5" data-testid="help-functions">
          <h2 className="font-display font-bold text-lg mb-3">{H.functionsTitle}</h2>
          <Accordion type="single" collapsible className="space-y-2">
            {H.functions.map((f, i) => {
              const Icon = FN_ICONS[i] || Sparkles;
              return (
                <AccordionItem key={i} value={`fn-${i}`} className="border border-white/5 rounded-xl px-3 bg-white/[0.02]" data-testid={`help-fn-${i}`}>
                  <AccordionTrigger className="hover:no-underline py-3">
                    <span className="flex items-center gap-2.5 text-left">
                      <span className="w-8 h-8 rounded-lg bg-primary/15 text-neon flex items-center justify-center shrink-0"><Icon size={16} /></span>
                      <span className="font-semibold text-sm">{f.name}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-3">
                    <div className="space-y-2 pl-1">
                      <p className="text-sm"><span className="text-neon font-medium">{L.what} </span>{f.what}</p>
                      <p className="text-sm"><span className="text-neon font-medium">{L.when} </span>{f.when}</p>
                      <p className="text-sm"><span className="text-neon font-medium">{L.how} </span>{f.how}</p>
                      <p className="text-xs text-[hsl(var(--muted-foreground))] rounded-lg bg-white/[0.03] border border-white/5 p-2.5"><span className="font-medium text-white">{L.example} : </span>{f.example}</p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </motion.div>

        {/* Examples */}
        <motion.div variants={item} className="lb-card p-5" data-testid="help-examples">
          <h2 className="font-display font-bold text-lg mb-3">{H.examplesTitle}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {H.examples.map((e, i) => (
              <div key={i} className="p-4 rounded-xl bg-white/[0.02] border border-white/5">
                <span className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full bg-neon/15 text-neon mb-2">{e.tag}</span>
                <p className="text-sm leading-relaxed">{e.text}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* FAQ */}
        <motion.div variants={item} className="lb-card p-5" data-testid="help-faq">
          <h2 className="font-display font-bold text-lg mb-3">{H.faqTitle}</h2>
          <Accordion type="single" collapsible className="space-y-2">
            {H.faq.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-white/5 rounded-xl px-3 bg-white/[0.02]" data-testid={`help-faq-${i}`}>
                <AccordionTrigger className="hover:no-underline py-3 text-sm font-medium text-left">{f.q}</AccordionTrigger>
                <AccordionContent className="pb-3 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        {/* Smart help */}
        <motion.div variants={item} className="lb-card p-5" data-testid="help-smart">
          <div className="flex items-center gap-2 mb-1">
            <Lightbulb size={18} className="text-amber-400" />
            <h2 className="font-display font-bold text-lg">{H.smartTitle}</h2>
          </div>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mb-3">{H.smartIntro}</p>
          <div className="flex gap-2">
            <Input data-testid="help-smart-input" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
              placeholder={H.smartPlaceholder} className="bg-white/5 border-white/10 h-11" />
            <Button data-testid="help-smart-ask" onClick={ask} disabled={loading || !q.trim()} className="rounded-xl h-11 px-4 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn shrink-0">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <><Send size={16} className="mr-1.5" /> {H.smartBtn}</>}
            </Button>
          </div>
          {(loading || answer) && (
            <div className="mt-4 rounded-xl bg-white/[0.02] border border-white/5 p-4" data-testid="help-smart-answer">
              {loading ? (
                <div className="space-y-2"><div className="skeleton h-4 w-3/4" /><div className="skeleton h-4 w-full" /><div className="skeleton h-4 w-2/3" /></div>
              ) : (
                <Markdown>{answer}</Markdown>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </Layout>
  );
}
